
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import PostContent from './components/PostContent';
import PostSidebar from './components/PostSidebar';
import { blogPosts } from '../../mocks/blogPosts';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const post = blogPosts.find((p) => p.id === slug);

  useSEO({
    title: post ? `${post.title} | Daliah Secret Blog` : 'Article Not Found | Daliah Secret',
    description: post?.excerpt || 'Blog article not found',
    keywords: post ? `${post.category}, Daliah Secret, beauty tips, fragrance guide` : '',
    canonical: post ? `/blog/${post.id}` : '/blog',
    ogType: 'article',
    ogImage: post?.image,
    schema: post
      ? generateWebPageSchema(post.title, post.excerpt, `/blog/${post.id}`)
      : undefined,
  });

  if (!post) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-32 pb-20 text-center max-w-7xl mx-auto px-6">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
            <i className="ri-article-line text-3xl text-gray-400"></i>
          </div>
          <h1 className="text-3xl font-serif text-gray-900 mb-3">Article Not Found</h1>
          <p className="text-gray-500 text-sm mb-8">The article you&apos;re looking for doesn&apos;t exist or has been moved.</p>
          <Link
            to="/blog"
            className="inline-block px-6 py-3 rounded-full bg-[#2E7D32] text-white text-sm font-medium hover:bg-[#1B5E20] transition-colors cursor-pointer whitespace-nowrap"
          >
            Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const currentIndex = blogPosts.findIndex((p) => p.id === slug);
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const text = post.title;
    const shareUrls: Record<string, string> = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    };
    window.open(shareUrls[platform], '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero Image */}
      <header className="pt-24">
        <div className="max-w-7xl mx-auto px-6 pt-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-gray-400 mb-6">
            <Link to="/" className="hover:text-[#2E7D32] transition-colors cursor-pointer">Home</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-[#2E7D32] transition-colors cursor-pointer">Blog</Link>
            <span>/</span>
            <span className="text-gray-600 truncate max-w-[200px]">{post.title}</span>
          </nav>

          <div className="mb-8">
            <span className="inline-block px-3 py-1 bg-[#E8F5E9] text-[#2E7D32] text-xs font-medium rounded-full mb-4 tracking-wider">
              {post.category}
            </span>
            <h1 className="text-4xl font-serif text-gray-900 mb-4 leading-tight max-w-3xl">
              {post.title}
            </h1>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                  <i className="ri-user-line text-[#2E7D32] text-sm"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">{post.author}</p>
                  <p className="text-[11px] text-gray-400">{post.authorRole}</p>
                </div>
              </div>
              <span className="w-px h-5 bg-gray-200"></span>
              <span className="text-xs text-gray-400">{post.date}</span>
              <span className="w-px h-5 bg-gray-200"></span>
              <span className="text-xs text-gray-400">{post.readTime}</span>
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden h-[420px] mb-12">
            <img
              src={post.image}
              alt={post.title}
              title={`${post.title} - Daliah Secret Blog`}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </header>

      {/* Content + Sidebar */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-3 gap-12">
            <div className="col-span-2">
              <PostContent content={post.content} />

              {/* Share */}
              <div className="mt-12 pt-8 border-t border-gray-100">
                <p className="text-xs text-gray-400 tracking-wider mb-3">SHARE THIS ARTICLE</p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleShare('twitter')}
                    className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-[#E8F5E9] transition-colors cursor-pointer"
                    aria-label="Share on Twitter"
                  >
                    <i className="ri-twitter-x-line text-sm text-gray-600"></i>
                  </button>
                  <button
                    onClick={() => handleShare('facebook')}
                    className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-[#E8F5E9] transition-colors cursor-pointer"
                    aria-label="Share on Facebook"
                  >
                    <i className="ri-facebook-circle-line text-sm text-gray-600"></i>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-[#E8F5E9] transition-colors cursor-pointer"
                    aria-label="Share on LinkedIn"
                  >
                    <i className="ri-linkedin-line text-sm text-gray-600"></i>
                  </button>
                </div>
              </div>

              {/* Prev / Next */}
              <div className="mt-10 grid grid-cols-2 gap-4">
                {prevPost ? (
                  <button
                    onClick={() => navigate(`/blog/${prevPost.id}`)}
                    className="flex items-center gap-3 p-4 rounded-xl bg-[#F9FBF7] border border-gray-100 hover:border-[#2E7D32]/30 transition-colors cursor-pointer text-left group"
                  >
                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 group-hover:bg-[#E8F5E9] transition-colors">
                      <i className="ri-arrow-left-s-line text-gray-500"></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-gray-400 tracking-wider mb-0.5">PREVIOUS</p>
                      <p className="text-sm font-medium text-gray-800 truncate">{prevPost.title}</p>
                    </div>
                  </button>
                ) : <div />}
                {nextPost ? (
                  <button
                    onClick={() => navigate(`/blog/${nextPost.id}`)}
                    className="flex items-center gap-3 p-4 rounded-xl bg-[#F9FBF7] border border-gray-100 hover:border-[#2E7D32]/30 transition-colors cursor-pointer text-right group justify-end"
                  >
                    <div className="min-w-0">
                      <p className="text-[10px] text-gray-400 tracking-wider mb-0.5">NEXT</p>
                      <p className="text-sm font-medium text-gray-800 truncate">{nextPost.title}</p>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 group-hover:bg-[#E8F5E9] transition-colors">
                      <i className="ri-arrow-right-s-line text-gray-500"></i>
                    </div>
                  </button>
                ) : <div />}
              </div>
            </div>

            <div className="col-span-1">
              <PostSidebar currentPostId={post.id} />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
